import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { FormDefinition, EncryptedFormPayload, FormType } from '../types/forms';

export async function getFormEncryptionKey(
  practitionerUid: string,
): Promise<{ publicKeyB64: string; algorithm: string } | null> {
  try {
    const snap = await getDoc(
      doc(db, 'public_listings', practitionerUid, 'publicProfile', 'forms'),
    );
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      publicKeyB64: data.publicKey as string,
      algorithm: data.algorithm as string,
    };
  } catch (err) {
    console.error('Error fetching form encryption key:', err);
    return null;
  }
}

export async function getFormByCode(
  practitionerUid: string,
  formCode: string,
): Promise<FormDefinition | null> {
  try {
    const snap = await getDocs(
      query(
        collection(db, 'public_listings', practitionerUid, 'forms'),
        where('formCode', '==', formCode),
      ),
    );
    if (snap.empty) return null;
    const d = snap.docs[0];
    const form = { id: d.id, ...d.data() } as FormDefinition;
    if (form.status === 'archived') return null;
    return form;
  } catch (err) {
    console.error('Error fetching form by code:', err);
    return null;
  }
}

export async function getApplicableForms(
  practitionerUid: string,
  serviceId: string,
): Promise<FormDefinition[]> {
  try {
    const snap = await getDocs(
      query(
        collection(db, 'public_listings', practitionerUid, 'forms'),
        where('status', '==', 'active'),
      ),
    );

    const allForms = snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
    })) as FormDefinition[];

    const newClientForms = allForms
      .filter(f => f.type === 'new_client')
      .sort((a, b) => a.name.localeCompare(b.name));

    const bookingForms = allForms.filter(
      f => f.type === 'booking' && f.attachedServiceIds?.includes(serviceId),
    );

    return [...newClientForms, ...bookingForms];
  } catch (err) {
    console.error('Error fetching applicable forms:', err);
    return [];
  }
}

export async function submitFormResponse(params: {
  practitionerId: string;
  token?: string;
  recaptchaToken?: string;
  responses: Array<{
    formId: string;
    formType: FormType;
    bookingId?: string;
    clientId?: string;
    ciphertext: string;
    nonce: string;
    ephemeralPublicKey: string;
    respondentName?: string;
    respondentPhone?: string;
  }>;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const callable = httpsCallable(functions, 'submitFormResponse');
    await callable(params);
    return { success: true };
  } catch (err: unknown) {
    console.error('Error submitting form response:', err);
    return {
      success: false,
      error: (err as { message?: string })?.message || 'Failed to submit form response',
    };
  }
}
